import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Star } from 'lucide-react';
import { useProducts } from '@/hooks/useProducts';
import { useCart } from '@/hooks/useCart';
import { useCityNavigation } from '@/hooks/useCityNavigation';
import { formatCurrency } from '@/lib/currency';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { optimizeImageUrl } from '@/utils/image-optimizer';

interface FlowerAuraProductsProps {
  language: string;
}

const FlowerAuraProducts = ({ language }: FlowerAuraProductsProps) => {
  const { selectedCity } = useDeliveryLocation();
  const { data: products, isLoading } = useProducts({ 
    featured: true,
    city: selectedCity?.slug
  });
  const { addItem } = useCart();
  const { navigateWithCity } = useCityNavigation();

  const translations = {
    en: {
      title: "Our Best Selling Flowers",
      subtitle: "Top Selections for Every Occasion",
      viewAll: "View All",
      egp: "EGP",
      earliestDelivery: "Earliest Delivery : Today",
      reviews: "reviews"
    },
    ar: {
      title: "أفضل وردنا مبيعاً",
      subtitle: "أفضل الاختيارات لكل مناسبة",
      viewAll: "عرض الكل",
      egp: "جنيه",
      earliestDelivery: "أقرب توصيل : اليوم",
      reviews: "تقييم"
    }
  };

  const t = translations[language as keyof typeof translations];

  const handleAddToCart = (product: any, e: React.MouseEvent) => {
    e.stopPropagation();
    addItem({
      id: product.id,
      name: language === 'ar' ? product.name_ar : product.name_en,
      price: product.price,
      image_url: product.image_url
    });
  };

  const handleProductClick = (product: any) => {
    navigateWithCity(`/products/${product.slug}`);
  };

  if (isLoading) {
    return (
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8" style={{ minHeight: '80px' }}>
            <div className={`${language === 'ar' ? 'text-right' : 'text-left'} animate-pulse`}>
              <div className="h-8 bg-muted rounded w-64 mb-2"></div>
              <div className="h-4 bg-muted rounded w-48"></div>
            </div>
            <div className="h-10 w-24 rounded-lg bg-muted animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-border bg-background">
                <div className="aspect-square bg-muted animate-pulse"></div>
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-muted rounded w-4/5 animate-pulse"></div>
                  <div className="h-5 bg-muted rounded w-2/5 animate-pulse"></div>
                  <div className="h-4 bg-muted rounded w-1/3 animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background" data-products-ready="true">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8" style={{ minHeight: '80px' }}>
          <div className={`${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{t.title}</h2>
            <p className="text-muted-foreground">{t.subtitle}</p>
          </div>
          <Button 
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 rounded-lg font-medium"
            onClick={() => navigateWithCity('/flowers')}
          >
            {t.viewAll}
          </Button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6">
          {products?.slice(0, 8).map((product) => (
            <Card 
              key={product.id}
              className="group cursor-pointer border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden"
              onClick={() => handleProductClick(product)}
            >
              <CardContent className="p-0">
                <div className="relative">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={optimizeImageUrl(product.image_url || '/unsplash/photo-1465146344425-f00d5f5c8f07@3x.webp', 400)}
                      alt={language === 'ar' ? product.name_ar : product.name_en}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                      width="400"
                      height="400"
                      style={{ aspectRatio: '1/1' }}
                    />
                  </div>
                  
                  {/* Additional images preview */}
                  {Array.isArray(product.images) && product.images.length > 0 && (
                    <div className="absolute bottom-2 right-2 flex gap-1">
                      {(product.images as string[]).slice(0, 2).map((img, index) => (
                        <div key={index} className="w-6 h-6 rounded border border-white shadow-sm overflow-hidden">
                          <img 
                            src={img}
                            alt={`${language === 'ar' ? product.name_ar : product.name_en} view ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            width="24"
                            height="24"
                            style={{ aspectRatio: '1/1' }}
                          />
                        </div>
                      ))}
                      {(product.images as string[]).length > 2 && (
                        <div className="w-6 h-6 rounded border border-white shadow-sm bg-primary/80 flex items-center justify-center">
                          <span className="text-xs text-primary-foreground font-medium">
                            +{(product.images as string[]).length - 2}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <button className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm rounded-full p-2 hover:scale-110 transition-all group/heart">
                    <Heart className="h-4 w-4 text-muted-foreground group-hover/heart:text-destructive transition-colors" />
                  </button>
                </div>

                <div className="p-4">
                  <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                    {language === 'ar' ? product.name_ar : product.name_en}
                  </h3>
                  
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-lg font-bold text-foreground">
                      {formatCurrency(product.price, { language: language as 'ar' | 'en' })}
                    </div>
                    {product.original_price && (
                      <div className="text-sm text-muted-foreground line-through">
                        {formatCurrency(product.original_price, { language: language as 'ar' | 'en' })}
                      </div>
                    )}
                  </div>

                  {product.rating > 0 && product.review_count > 0 && (
                    <div className="flex items-center mb-2">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-warning fill-current" />
                        <span className="text-sm font-medium ml-1">{product.rating}</span>
                      </div>
                      <span className="text-sm text-muted-foreground ml-1">
                        ({product.review_count} {t.reviews})
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FlowerAuraProducts;